import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reviews`);
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div data-testid="loading-state" className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const featuredReview = reviews[0];
  const otherReviews = reviews.slice(1, 7);

  return (
    <div data-testid="home-page" className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-muted py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-3xl">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Honest Reviews.
              <br />
              Smart Choices.
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8">
              In-depth product evaluations to help you make informed purchasing decisions. We test, compare, and recommend the best consumer products.
            </p>
            <Link
              to="/reviews"
              data-testid="hero-cta-btn"
              className="inline-block px-8 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:opacity-90 smooth-transition"
            >
              Explore Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Review - Bento Grid */}
      {featuredReview && (
        <section className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
          <h2 className="font-serif text-3xl font-bold mb-8">Featured Review</h2>
          <Link
            to={`/review/${featuredReview.slug}`}
            data-testid="featured-review-card"
            className="block group"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border border-border p-8 hover-scale smooth-transition">
              <div className="md:col-span-7">
                <img
                  src={featuredReview.featured_image}
                  alt={featuredReview.title}
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="md:col-span-5 flex flex-col justify-center">
                <div className="mb-4">
                  <span className="text-sm font-medium text-primary">
                    {featuredReview.category?.name}
                  </span>
                </div>
                <h3 className="font-serif text-3xl font-bold mb-4 group-hover:text-primary smooth-transition">
                  {featuredReview.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {featuredReview.description.substring(0, 200)}...
                </p>
                <div className="flex items-center space-x-4 mb-4">
                  {renderStars(featuredReview.rating)}
                  <span className="text-lg font-semibold">{featuredReview.rating}/5</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  By {featuredReview.author}
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Latest Reviews Grid */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-24">
        <h2 className="font-serif text-3xl font-bold mb-8">Latest Reviews</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherReviews.map((review) => (
            <Link
              key={review.id}
              to={`/review/${review.slug}`}
              data-testid={`review-card-${review.slug}`}
              className="group block"
            >
              <div className="border border-border overflow-hidden hover-scale smooth-transition">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={review.featured_image}
                    alt={review.title}
                    className="w-full h-full object-cover group-hover:scale-105 smooth-transition"
                  />
                </div>
                <div className="p-6">
                  <div className="mb-3">
                    <span className="text-xs font-medium text-primary uppercase tracking-wide">
                      {review.category?.name}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-bold mb-3 group-hover:text-primary smooth-transition">
                    {review.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {review.description.substring(0, 120)}...
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {renderStars(review.rating)}
                      <span className="text-sm font-semibold">{review.rating}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{review.author}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;